import React from 'react';
import useLocale from '@/hooks/useLocale';
import type { LocaleWithGatorProtectUpgradeCycle } from '../../../gatorProtect.types';
import {
  getSelectedPlanInfo,
  type GetSelectedPlanInfoInput,
} from '../../../utils/getSelectedPlanInfo';
import * as Styles from '../ProtectionCard.styles';
import {
  ProtectionCardBody,
  ProtectionCardShield,
  ProtectionCardTextBlock,
  ProtectionCardActions,
  ProtectionCardButton,
  ProtectionCardMonarx,
} from '../ProtectionCardCompound';

export const UPGRADE_CYCLE_PROTECTION_CARD_TYPE = 'upgradeCycle' as const;
export type UpgradeCycleProtectionCardType = typeof UPGRADE_CYCLE_PROTECTION_CARD_TYPE;

export type UpgradeCycleVariantProps = {
  testId?: string;
  discountPercent: number;
  isButtonLoading?: boolean;
  onButtonClick?: () => void;
  upgradeCycleSelectedPlan?: GetSelectedPlanInfoInput | null;
};

const UpgradeCycleVariant: React.FC<UpgradeCycleVariantProps> = ({
  testId = 'protection-card',
  discountPercent,
  isButtonLoading = false,
  onButtonClick,
  upgradeCycleSelectedPlan,
}) => {
  const { gatorProtect } = useLocale() as LocaleWithGatorProtectUpgradeCycle;
  const raw = gatorProtect?.summary?.protectionCard?.upgradeCycle;
  const fromAnnualHosting = getSelectedPlanInfo(upgradeCycleSelectedPlan).isAnnualHostingCycle;
  if (
    !raw
    || typeof raw.getTitle !== 'function'
    || typeof raw.getDescriptionHtml !== 'function'
    || typeof raw.getActionLabel !== 'function'
  ) {
    return null;
  }

  const title = raw.getTitle(fromAnnualHosting);
  const descriptionHtml = raw.getDescriptionHtml(discountPercent, fromAnnualHosting);
  if (!title) {
    return null;
  }

  const actionsTestId = `${testId}__actions`;

  return (
    <Styles.InverseCard data-component="ProtectionCardCompound" data-testid={testId}>
      <ProtectionCardBody>
        <ProtectionCardShield title={title} />
        <ProtectionCardTextBlock>
          <Styles.InverseTitle>{title}</Styles.InverseTitle>
          {Boolean(descriptionHtml) && (
            <Styles.InverseDescription
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}
          <ProtectionCardActions actionsTestId={actionsTestId}>
            <ProtectionCardButton
              variant="inverse"
              hasMultipleButtons={false}
              onClick={onButtonClick}
              testId={`${testId}__action-subscribeAnnualWithDiscount`}
              loading={isButtonLoading}
            >
              {raw.getActionLabel(discountPercent, fromAnnualHosting)}
            </ProtectionCardButton>
          </ProtectionCardActions>
        </ProtectionCardTextBlock>
      </ProtectionCardBody>
      <ProtectionCardMonarx title={title} />
    </Styles.InverseCard>
  );
};

export default UpgradeCycleVariant;
